import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useProjectById } from "~/entities/project";
import { useScenesByProject } from "~/entities/scene";
import { useActiveWorkspace } from "~/entities/workspace";
import { useDraftChatForAttachments } from "~/features/create-chat/useDraftChatForAttachments";
import { useSubmitLocalMessage } from "~/features/send-local-message/useSubmitLocalMessage";
import { useStartGeneration } from "~/features/start-generation/useStartGeneration";
import { useActiveProjectId, useActiveProjectStore } from "~/shared/store/useActiveProjectStore";
import { NEW_CHAT_DRAFT_KEY, useDraftChatStore } from "~/shared/store/useDraftChatStore";

import { EmptyState } from "~/widgets/chat-messages/EmptyState";
import { PromptBox } from "~/widgets/chat-prompt/PromptBox";

import { ChatHeader } from "./ChatHeader";
import { ChatShellLayout } from "./ChatShellLayout";
import { useAutoOpenSettingsOnDesktop } from "./useAutoOpenSettingsOnDesktop";

import type { AttachmentCreate } from "~/entities/chat";

export function ChatShellEmpty() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { activeWorkspace, activeWorkspaceId } = useActiveWorkspace();
    const workspaceId = activeWorkspaceId ?? undefined;
    const workspaceName = activeWorkspace?.workspace.name;
    const urlProjectId = searchParams.get("projectId") ?? undefined;
    const urlSceneId = searchParams.get("sceneId") ?? undefined;
    const activeProjectId = useActiveProjectId(workspaceId);
    const setActiveProject = useActiveProjectStore((s) => s.setActive);
    // Scope чата задаём с приоритетом URL > активный из store.
    // Это нужно чтобы ссылка `/chat?projectId=X` из ChatsTab гарантированно
    // создала чат именно в X, а не в последнем активном проекте.
    const targetProjectId = urlProjectId ?? activeProjectId ?? undefined;
    const targetSceneId = urlSceneId;
    // Крошки черновика должны отражать тот же scope, с которым будет создан
    // чат (project + scene из URL): «Workspace > Project > Scene > Untitled chat».
    // Резолвим имена так же, как useChatShellContext для открытого чата.
    const projectQuery = useProjectById(targetProjectId);
    const scenesQuery = useScenesByProject(targetProjectId, { includeArchived: true });
    const sceneName = useMemo(
        () => scenesQuery.data?.data.find((s) => s.id === targetSceneId)?.name,
        [scenesQuery.data, targetSceneId],
    );
    const {
        draftChatId,
        run: runDraftChat,
        isPending: draftChatPending,
    } = useDraftChatForAttachments({
        workspaceId,
        projectId: targetProjectId,
        sceneId: targetSceneId,
    });
    const submitLocal = useSubmitLocalMessage();
    const startGeneration = useStartGeneration();
    const moveDraftText = useDraftChatStore((s) => s.moveDraftText);

    useAutoOpenSettingsOnDesktop();

    // Если пришли из проекта (URL содержит projectId) — синхронизируем
    // active project store, чтобы sidebar показывал актуальный проект.
    useEffect(() => {
        if (!workspaceId || !urlProjectId) return;
        if (urlProjectId !== activeProjectId) {
            setActiveProject(workspaceId, urlProjectId);
        }
    }, [workspaceId, urlProjectId, activeProjectId, setActiveProject]);

    const handleSubmit = async (prompt: string, attachments: AttachmentCreate[]) => {
        if (!workspaceId || draftChatPending) return;
        // Переиспускает уже созданный заранее draft (тот же id, что в промпт-боксе).
        const { chat } = await runDraftChat({
            workspaceId,
            projectId: targetProjectId,
            sceneId: targetSceneId,
        });
        // Ждём postMessage перед navigate'ом: если он упадёт (например на
        // балансе), пользователь остаётся на /chat с заполненным promptbox,
        // а chat-сирота уберёт hourly orphan_chat_cleanup на бэке. Если
        // успешно — navigate + fire-and-forget startGeneration.
        const message = await submitLocal(chat.id, prompt, attachments);
        if (!message) return;
        // Набранный текст привязан к пустому экрану, пока чата нет; переносим его
        // на созданный, иначе navigate ниже размонтирует бар и человек потеряет
        // запрос, который собирался поправить и запустить снова.
        moveDraftText(draftChatId ?? NEW_CHAT_DRAFT_KEY, chat.id);
        void navigate(`/chat/${chat.id}`);
        void startGeneration({
            chatUuid: chat.id,
            messageId: message.id,
            inputAttachments: message.attachments ?? [],
        });
    };

    return (
        <ChatShellLayout
            header={
                <ChatHeader
                    workspaceName={workspaceName}
                    workspaceId={activeWorkspaceId ?? null}
                    chatTitle={null}
                    projectName={projectQuery.data?.name}
                    projectId={targetProjectId ?? null}
                    sceneName={sceneName}
                    sceneId={targetSceneId ?? null}
                    canWrite={false}
                />
            }
            main={
                <>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <EmptyState />
                    </div>
                    <PromptBox chatId={draftChatId} onSubmit={handleSubmit} />
                </>
            }
        />
    );
}
